import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { PageCanvas, PageHeader } from "@/components/page-canvas";
import {
  RecordDescription,
  RecordLink,
  RecordMetadata,
  RecordRow,
  RecordTitle,
} from "@/components/record-link";
import { Button } from "@/components/ui/button";
import AppLayout from "@/layouts/app_layout";
import {
  locationDashboardPath,
  locationJobPath,
  locationJobsPath,
  locationRequestsPath,
  locationDetailingProductsPath,
  locationCalendarPath,
  locationBaysPath,
  locationServicesPath,
  newLocationJobPath,
} from "@/routes";
import { dateTime } from "../jobs/types";
import type { Stage } from "../jobs/types";

type Attention = {
  stages: Record<Stage, number>;
  today: {
    date: string;
    more: boolean;
    total: number;
    jobs: {
      id: string;
      starts_at: string;
      time_zone: string;
      bay_name: string;
      work_state: string;
      customer: string;
      vehicle: {
        make: string;
        model: string;
        registration_number: string | null;
      };
      capacity: { state: string };
    }[];
  };
  requests: number;
  overdue_advances: number;
  low_stock: number;
  setup: { administrator: boolean; bays: boolean; offerings: boolean };
};
export default function Dashboard({ attention }: { attention: Attention }) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const id = auth.location.id;
  const queues = [
    {
      key: "requests",
      label: t("home_attention.requests"),
      count: attention.requests,
      href: locationRequestsPath(id, { state: "new" }),
    },
    ...(
      ["to_do", "in_progress", "ready_for_collection", "billing"] as Stage[]
    ).map((stage) => ({
      key: stage,
      label: t(`job_board.stages.${stage}`),
      count: attention.stages[stage],
      href: locationJobsPath(id, { stage }),
    })),
    {
      key: "overdue",
      label: t("home_attention.overdue"),
      count: attention.overdue_advances,
      href: locationJobsPath(id, { advance_overdue: "1" }),
    },
    {
      key: "stock",
      label: t("home_attention.stock"),
      count: attention.low_stock,
      href: locationDetailingProductsPath(id, { low_stock: "1" }),
    },
  ];
  const missingSetup = !attention.setup.bays || !attention.setup.offerings;
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("navigation.home"), href: locationDashboardPath(id) },
      ]}
    >
      <Head title={t("navigation.home")} />
      <PageCanvas>
        <PageHeader
          title={t("navigation.home")}
          description={`${auth.location.name} · ${auth.account?.name}`}
          actions={
            <>
              <Button
                variant="outline"
                render={<Link href={locationCalendarPath(id)} />}
              >
                {t("navigation.schedule")}
              </Button>
              <Button
                render={
                  <Link
                    href={newLocationJobPath(id, {
                      composition: { origin: "home" },
                    })}
                  />
                }
              >
                {t("jobs.create")}
              </Button>
            </>
          }
        />
        <section className="space-y-4">
          <h2 className="text-section font-semibold">
            {t("home_attention.title")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("home_attention.help")}
          </p>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {queues.map((queue) => (
              <RecordRow
                key={queue.key}
                className="min-h-16 rounded-lg border bg-card p-4"
                href={queue.href}
                data-attention={queue.key}
              >
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <RecordMetadata className="min-w-0 underline underline-offset-4">
                    {queue.label}
                  </RecordMetadata>
                  <span
                    data-record-role="value"
                    className="shrink-0 text-2xl font-semibold tabular-nums"
                  >
                    {queue.count}
                  </span>
                </div>
              </RecordRow>
            ))}
          </div>
        </section>
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-section font-semibold">
              {t("home_attention.today")}
            </h2>
            <RecordLink
              href={locationCalendarPath(id, {
                date: attention.today.date,
                view: "day",
              })}
            >
              {t("home_attention.view_schedule")}
            </RecordLink>
          </div>
          {!attention.today.jobs.length && (
            <p className="text-muted-foreground">
              {t("home_attention.today_empty")}
            </p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            {attention.today.jobs.map((job) => (
              <RecordRow
                key={job.id}
                className="rounded-lg border bg-card p-4 text-sm"
                href={locationJobPath(id, job.id)}
              >
                <div className="min-w-0 space-y-2">
                  <RecordTitle>
                    {job.vehicle.make} {job.vehicle.model}
                    {job.vehicle.registration_number
                      ? ` · ${job.vehicle.registration_number}`
                      : ""}
                  </RecordTitle>
                  <RecordDescription>{job.customer}</RecordDescription>
                  <div className="space-y-1">
                    <RecordMetadata>
                      {dateTime(job.starts_at, job.time_zone, i18n.locale)} ·{" "}
                      {job.bay_name}
                    </RecordMetadata>
                    <RecordMetadata>
                      {t(`job_board.stages.${job.work_state}`)}
                      {job.capacity.state === "in_progress"
                        ? ` · ${t("calendar.occupied")}`
                        : ""}
                    </RecordMetadata>
                  </div>
                </div>
              </RecordRow>
            ))}
          </div>
          {attention.today.more && (
            <p className="text-muted-foreground text-sm">
              {t("home_attention.today_more", { count: attention.today.total })}
            </p>
          )}
        </section>
        {missingSetup && (
          <section className="space-y-4 rounded-lg border bg-card p-5">
            <h2 className="text-section font-semibold">
              {t("home_attention.setup")}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t(
                attention.setup.administrator
                  ? "home_attention.setup_help"
                  : "home_attention.ask_admin",
              )}
            </p>
            {attention.setup.administrator && (
              <ul className="space-y-3">
                {!attention.setup.bays && (
                  <li>
                    <RecordLink href={locationBaysPath(id)}>
                      {t("home_attention.add_bay")}
                    </RecordLink>
                  </li>
                )}
                {!attention.setup.offerings && (
                  <li>
                    <RecordLink href={locationServicesPath(id)}>
                      {t("home_attention.add_service")}
                    </RecordLink>
                  </li>
                )}
              </ul>
            )}
          </section>
        )}
      </PageCanvas>
    </AppLayout>
  );
}
